import { useAuth } from "@/context/AuthContext";
import WriteButton from "@/components/WriteButton";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Bluetooth, Wifi, Save, Loader2, Contrast } from "lucide-react";

export default function PrinterSettings() {
  const { readOnly } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { ...form, eth_port: Number(form.eth_port || 9100) });
      toast.success("Pengaturan printer disimpan");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const [applying, setApplying] = useState(false);
  const applyDensity = async () => {
    setApplying(true);
    try {
      await api.put("/settings", { ...form, eth_port: Number(form.eth_port || 9100) });
      await api.post("/print/density");
      toast.success("Kepekatan diterapkan ke printer. Coba cetak ulang struk.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplying(false);
    }
  };

  if (!form) return (<><PageHeader title="Pengaturan Printer" /><div className="flex-1 flex items-center justify-center text-slate-500">Memuat…</div></>);

  return (
    <>
      <PageHeader title="Pengaturan Printer" subtitle="Struk thermal · Bluetooth & Ethernet">
        <WriteButton data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
        </WriteButton>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5" data-testid="printer-settings">
        <div className="max-w-2xl space-y-5">
          {/* Store identity */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Printer className="h-4 w-4 text-emerald-600" /> Header & Footer Struk</div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Nama Toko</Label>
              <Input disabled={readOnly} data-testid="set-store-name" value={form.store_name} onChange={(e) => set("store_name", e.target.value)} className="bg-[#F7F9FB] border-slate-300" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Alamat</Label>
                <Input disabled={readOnly} data-testid="set-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="bg-[#F7F9FB] border-slate-300" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Telepon</Label>
                <Input disabled={readOnly} data-testid="set-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-[#F7F9FB] border-slate-300" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Catatan Kaki (Footer)</Label>
              <Textarea disabled={readOnly} data-testid="set-footer" value={form.footer || ""} onChange={(e) => set("footer", e.target.value)} rows={2} className="bg-[#F7F9FB] border-slate-300" />
            </div>
          </section>

          {/* Paper & mode */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="text-sm font-semibold text-slate-600">Perangkat & Kertas</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Ukuran Kertas</Label>
                <Select disabled={readOnly} value={form.paper} onValueChange={(v) => set("paper", v)}>
                  <SelectTrigger data-testid="set-paper" className="bg-[#F7F9FB] border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                    <SelectItem value="58">58mm</SelectItem>
                    <SelectItem value="80">80mm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Metode Utama</Label>
                <Select disabled={readOnly} value={form.mode} onValueChange={(v) => set("mode", v)}>
                  <SelectTrigger data-testid="set-mode" className="bg-[#F7F9FB] border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                    <SelectItem value="browser">Dialog Browser</SelectItem>
                    <SelectItem value="bluetooth">Bluetooth</SelectItem>
                    <SelectItem value="ethernet">Ethernet / Network</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="text-sm text-slate-700">Cetak otomatis setelah bayar</Label>
                <p className="text-[11px] text-slate-500">Struk langsung tercetak begitu pembayaran selesai.</p>
              </div>
              <Switch disabled={readOnly} data-testid="set-autoprint" checked={form.auto_print} onCheckedChange={(v) => set("auto_print", v)} />
            </div>
          </section>

          {/* Bluetooth info */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Bluetooth className="h-4 w-4 text-blue-400" /> Printer Bluetooth</div>
            <p className="text-xs text-slate-500">Saat mencetak, tekan tombol <b>Bluetooth</b> di struk lalu pilih printer Anda. Berfungsi di Chrome (Android/desktop) via Web Bluetooth — cocok untuk printer thermal BLE di tablet.</p>
          </section>

          {/* Ethernet */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Wifi className="h-4 w-4 text-emerald-600" /> Printer Ethernet / Network</div>
            <div className="grid grid-cols-[1fr_120px] gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">IP Printer</Label>
                <Input disabled={readOnly} data-testid="set-eth-ip" value={form.eth_ip || ""} onChange={(e) => set("eth_ip", e.target.value)} placeholder="192.168.1.100" className="bg-[#F7F9FB] border-slate-300 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Port</Label>
                <Input disabled={readOnly} data-testid="set-eth-port" type="number" value={form.eth_port} onChange={(e) => set("eth_port", e.target.value)} className="bg-[#F7F9FB] border-slate-300 font-mono" />
              </div>
            </div>
            <p className="text-[11px] text-amber-600/90">Catatan: server aplikasi berjalan di cloud, jadi printer Ethernet harus dapat dijangkau dari internet (public IP / port-forward, port default 9100). Untuk printer LAN lokal, gunakan Bluetooth.</p>
          </section>

          {/* Kepekatan (Epson) */}
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="density-section">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Contrast className="h-4 w-4 text-emerald-600" /> Kepekatan Cetak (Epson TM-T82X)</div>
            <p className="text-xs text-slate-500">Struk dikirim sebagai gambar agar pekat. Jika masih kurang hitam, naikkan kepekatan &amp; perlambat kecepatan lalu tekan <b>Terapkan ke Printer</b>. Pengaturan tersimpan permanen di printer.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Kepekatan (Density)</Label>
                <Select disabled={readOnly} value={String(form.density ?? 6)} onValueChange={(v) => set("density", Number(v))}>
                  <SelectTrigger data-testid="set-density" className="bg-[#F7F9FB] border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                    <SelectItem value="0">100% (Normal)</SelectItem>
                    <SelectItem value="1">105%</SelectItem>
                    <SelectItem value="2">110%</SelectItem>
                    <SelectItem value="3">115%</SelectItem>
                    <SelectItem value="4">120%</SelectItem>
                    <SelectItem value="5">125%</SelectItem>
                    <SelectItem value="6">130% (Paling Pekat)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Kecepatan Cetak</Label>
                <Select disabled={readOnly} value={String(form.print_speed ?? 2)} onValueChange={(v) => set("print_speed", Number(v))}>
                  <SelectTrigger data-testid="set-speed" className="bg-[#F7F9FB] border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                    <SelectItem value="1">Level 1 (Paling lambat, paling pekat)</SelectItem>
                    <SelectItem value="2">Level 2</SelectItem>
                    <SelectItem value="3">Level 3</SelectItem>
                    <SelectItem value="5">Level 5</SelectItem>
                    <SelectItem value="10">Level 10 (Paling cepat)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <WriteButton data-testid="apply-density-btn" onClick={applyDensity} disabled={applying} variant="outline" className="w-full gap-2 border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10">
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Contrast className="h-4 w-4" />} Terapkan Kepekatan ke Printer
            </WriteButton>
          </section>
        </div>
      </div>
    </>
  );
}
